import { createApp } from "vue";
import store from "./store";
import "../node_modules/flowbite-vue/dist/index.css";
import App from "./App.vue";
import { createRouter, createWebHistory } from "vue-router";
import "./index.css";
import Toast from "vue-toastification";
import "vue-toastification/dist/index.css";
import "@/assets/css/main.css";
import "@/assets/css/cta-buttons.css";
import { createImageKitVue } from "imagekit-vue3";
import LoadScript from "vue-plugin-load-script";
import Vue3Geolocation from "vue3-geolocation";
import { isBioOnlyDomain } from "@/utils/siteMode";
function applyRuntimeEnv() {
  const runtime = window.__env_config || {};

  window.__VUE_APP_IP_ADDRESS =
    runtime.VUE_APP_IP_ADDRESS || "https://property-kgva.onrender.com";
  window.__VUE_APP_GOOGLE_CLIENT_ID = runtime.VUE_APP_GOOGLE_CLIENT_ID || "";
  window.__VUE_APP_IMAGEKIT_URL_ENDPOINT =
    runtime.VUE_APP_IMAGEKIT_URL_ENDPOINT || "";
  window.__VUE_APP_IMAGEKIT_PUBLIC_KEY =
    runtime.VUE_APP_IMAGEKIT_PUBLIC_KEY || "";

  try {
    if (typeof process !== "undefined" && process.env) {
      window.__VUE_APP_IP_ADDRESS =
        process.env.VUE_APP_IP_ADDRESS || window.__VUE_APP_IP_ADDRESS;
      window.__VUE_APP_GOOGLE_CLIENT_ID =
        process.env.VUE_APP_GOOGLE_CLIENT_ID || window.__VUE_APP_GOOGLE_CLIENT_ID;
      window.__VUE_APP_IMAGEKIT_URL_ENDPOINT =
        process.env.VUE_APP_IMAGEKIT_URL_ENDPOINT ||
        window.__VUE_APP_IMAGEKIT_URL_ENDPOINT;
      window.__VUE_APP_IMAGEKIT_PUBLIC_KEY =
        process.env.VUE_APP_IMAGEKIT_PUBLIC_KEY ||
        window.__VUE_APP_IMAGEKIT_PUBLIC_KEY;
    }
  } catch (e) {
    console.warn("Could not access process.env variables", e);
  }

  if (window.__ENV) {
    window.__ENV.VUE_APP_IP_ADDRESS = window.__VUE_APP_IP_ADDRESS;
  }
}

applyRuntimeEnv();

// We don't handle import.meta here because it would cause linting errors
// This is handled in our environment.js utility instead

const app = createApp(App);

// Lazy load components
const HomePage = () => import("./components/HelloWorld.vue");
const ViewListingSection = () => import("./components/ViewListingSection.vue");
const PageNotFound = () => import("./components/PageNotFound.vue");
const LoginSection = () => import("./components/LoginSection.vue");
const DashboardSection = () => import("./components/shop/DashboardSection.vue");
const ListingSection = () => import("./components/ListingSection.vue");
const ListingsSection = () =>
  import("./components/shop/admin/ListingsSection.vue");
const NewListingSection = () =>
  import("./components/shop/admin/NewListingSection.vue");
const EditListingSection = () =>
  import("./components/shop/admin/EditListingSection.vue");
const PropertyTypeSection = () =>
  import("./components/shop/admin/PropertyTypeSection.vue");
const LocationsSection = () =>
  import("./components/shop/admin/LocationsSection.vue");
const CampaignsSection = () =>
  import("./components/shop/admin/CampaignsSection.vue");
const AdminProfileSection = () =>
  import("./components/shop/admin/ProfileSection.vue");
const BioSection = () => import("./components/BioSection.vue");
const TestimonialsSection = () => import("./views/admin/Testimonials.vue");
const PrivacyPolicy = () => import("./views/PrivacyPolicy.vue");
const TermsAndConditions = () => import("./views/TermsAndConditions.vue");
const ListWithUs = () => import("./views/ListWithUs.vue");
const ListingSubmissions = () => import("./views/admin/ListingSubmissions.vue");
const ForgotPassword = () => import("./components/ForgotPassword.vue");
const ResetPassword = () => import("./components/ResetPassword.vue");

const router = createRouter({
  history: createWebHistory(),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ left: 0, top: 0, behavior: "smooth" });
      }, 500);
    });
  },
  routes: [
    {
      path: "/",
      component: HomePage,
    },
    {
      path: "/privacy-policy",
      component: PrivacyPolicy,
      meta: {
        name: "Privacy Policy",
      },
    },
    {
      path: "/terms-and-conditions",
      component: TermsAndConditions,
      meta: {
        name: "Terms and Conditions",
      },
    },
    {
      path: "/list-with-us",
      component: ListWithUs,
      meta: {
        name: "List With Us",
      },
    },
    {
      path: "/listings",
      component: ListingSection,
    },
    {
      path: "/listings/:id",
      component: ViewListingSection,
    },
    {
      path: "/bio",
      component: BioSection,
    },
    {
      path: "/admin",
      meta: {
        requiresAuth: true,
      },
      children: [
        {
          path: "/admin",
          component: DashboardSection,
          meta: {
            name: "Dashboard",
            requiresAuth: true,
          },
        },
        {
          path: "/admin/listings",
          component: ListingsSection,
          meta: {
            name: "Listings",
            requiresAuth: true,
          },
        },
        {
          path: "/admin/listings/new",
          title: "New Listing",
          component: NewListingSection,
          meta: {
            name: "New Listing",
            requiresAuth: true,
          },
        },
        {
          path: "/admin/listings/edit/:id",
          component: EditListingSection,
          meta: {
            name: "Edit Listing",
            requiresAuth: true,
          },
        },
        {
          path: "/admin/property-types",
          component: PropertyTypeSection,
          meta: {
            name: "Property Types",
            requiresAuth: true,
          },
        },
        {
          path: "/admin/campaigns",
          component: CampaignsSection,
          meta: {
            name: "Campaigns",
            requiresAuth: true,
          },
        },
        {
          path: "/admin/profile",
          component: AdminProfileSection,
          meta: {
            name: "Profile",
            requiresAuth: true,
          },
        },
        {
          path: "/admin/locations",
          component: LocationsSection,
          meta: {
            name: "Locations",
            requiresAuth: true,
          },
        },
        {
          path: "/admin/testimonials",
          component: TestimonialsSection,
          meta: {
            name: "Testimonials",
            requiresAuth: true,
          },
        },
        {
          path: "/admin/submissions",
          name: "ListingSubmissions",
          component: ListingSubmissions,
          meta: {
            name: "Listing Submissions",
            requiresAuth: true,
          },
        },
      ],
    },
    { path: "/:pathMatch(.*)*", name: "NotFound", component: PageNotFound },
    {
      path: "/login",
      component: LoginSection,
    },
    {
      path: "/forgot-password",
      component: ForgotPassword,
      meta: {
        name: "Forgot Password",
      },
    },
    {
      path: "/reset-password/:token",
      component: ResetPassword,
      meta: {
        name: "Reset Password",
      },
    },
  ],
});

router.beforeEach(async (to, from, next) => {
  if (isBioOnlyDomain() && to.path !== "/bio") {
    return next({ path: "/bio", replace: true });
  }

  if (to.matched.some((record) => record.meta.requiresAuth)) {
    var token = JSON.parse(window.localStorage.getItem("token"));
    if (token) {
      var today = new Date();
      var tokenExpiry = new Date(0);
      tokenExpiry.setUTCSeconds(token.expires);
      if (tokenExpiry < today) {
        window.localStorage.removeItem("user");
        window.localStorage.removeItem("token");
        store.commit("dropUserInfo");
        next("/login?redirect=" + encodeURIComponent(to.fullPath));
      }
    }

    if (!token) {
      next("/login?redirect=" + encodeURIComponent(to.fullPath));
    } else {
      next();
    }
  } else {
    next();
  }
});

router.afterEach((to) => {
  const path = isBioOnlyDomain() ? "/bio" : to.path;
  const baseUrl = isBioOnlyDomain()
    ? `https://${window.location.hostname}`
    : "https://www.zulekha.co.ke";
  const canonicalHref = baseUrl + path;
  let link = document.querySelector("link[rel='canonical']");
  if (link) {
    link.setAttribute("href", canonicalHref);
  } else {
    link = document.createElement("link");
    link.setAttribute("rel", "canonical");
    link.setAttribute("href", canonicalHref);
    document.head.appendChild(link);
  }
});

app.use(Vue3Geolocation);
app.use(LoadScript);
app.use(store);
app.use(router);
app.use(Toast, {
  transition: "Vue-Toastification__bounce",
  maxToasts: 20,
  newestOnTop: true,
});

app.use(
  createImageKitVue({
    urlEndpoint: window.__VUE_APP_IMAGEKIT_URL_ENDPOINT || "",
    publicKey: window.__VUE_APP_IMAGEKIT_PUBLIC_KEY || "",
  })
);

app.mount("#app");
